import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    company: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        company: user.company || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get('/api/user/profile');
        setFullProfile(response.data.user);
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    };
    fetchProfile();
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage(null);
    try {
      const result = await updateProfile(profileData);
      if (result.success) {
        setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setProfileMessage({ type: 'error', text: result.message || 'Failed to update profile' });
      }
    } catch (err) {
      setProfileMessage({ type: 'error', text: 'An error occurred while updating your profile' });
    }
    setProfileLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordLoading(false);
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setPasswordLoading(false);
      return;
    }

    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPasswordMessage({ type: 'error', text: result.message || 'Failed to change password' });
      }
    } catch (err) {
      setPasswordMessage({ type: 'error', text: 'An error occurred while changing your password' });
    }
    setPasswordLoading(false);
  };

  const profile = fullProfile || user || {};
  const plan = profile.plan || 'free';
  const planLabel = plan === 'free' ? 'Free' : plan === 'starter' ? 'Starter' : plan === 'business' ? 'Business' : plan === 'enterprise' ? 'Enterprise' : plan.charAt(0).toUpperCase() + plan.slice(1);
  const subscriptionStatus = profile.subscriptionStatus || 'inactive';
  const scansThisMonth = profile.scansThisMonth || 0;
  const planLimits = profile.planLimits || {};
  const scansLimit = planLimits.scansPerMonth || (plan === 'free' ? 1 : 'Unlimited');

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getInitials = () => {
    const f = profile.firstName || '';
    const l = profile.lastName || '';
    return (f.charAt(0) + l.charAt(0)).toUpperCase() || '?';
  };

  const getPlanColor = () => {
    switch(plan) {
      case 'starter': return 'from-blue-500 to-blue-600';
      case 'business': return 'from-purple-500 to-purple-600';
      case 'enterprise': return 'from-amber-500 to-amber-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPlanBadgeColor = () => {
    switch(plan) {
      case 'starter': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'business': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'enterprise': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusColor = () => {
    switch(subscriptionStatus) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400';
      case 'trialing': return 'bg-blue-500/20 text-blue-400';
      case 'past_due': return 'bg-red-500/20 text-red-400';
      case 'canceled': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'billing', label: 'Billing & Plan', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { id: 'security', label: 'Security', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' }
  ];


  const PROFILE_FAQ = [
    { q: "How do I update my account information?", a: "Navigate to your Profile page to update your name, email address, company information, and profile picture. Changes to your email require verification through a confirmation link sent to your new address. Your profile information is used across the platform for report headers, team identification, and communication preferences." },
    { q: "How do I change my subscription plan?", a: "You can upgrade or downgrade your SERPfinity subscription from the Profile settings page. View available plans, compare features, and switch with a single click. Upgrades take effect immediately with prorated billing, while downgrades apply at the start of your next billing cycle. Your existing data and projects are preserved during plan changes." },
    { q: "How do I manage notification preferences?", a: "SERPfinity offers granular notification controls. From your Profile settings, configure which alerts you receive via email, in-app notifications, or both. Set thresholds for ranking change alerts, choose site audit notification frequency, enable or disable AI visibility alerts, and customize digest email schedules to match your workflow preferences." },
    { q: "How do I add team members to my account?", a: "From the Team section in your Profile settings, invite team members by entering their email addresses and assigning appropriate roles. Team members receive invitation emails to create their accounts and gain immediate access to assigned projects. You can modify roles, revoke access, or transfer project ownership at any time from the team management panel." },
    { q: "Is my account data secure?", a: "SERPfinity takes security seriously. All data is encrypted in transit using TLS and at rest using AES-256 encryption. We support two-factor authentication for account protection, maintain regular security audits, and follow industry best practices for data protection. Your SEO data and competitive intelligence remain strictly confidential and are never shared with third parties." },
    { q: "How do I export or delete my account data?", a: "SERPfinity provides data export capabilities from the Profile settings page. Export your ranking history, audit results, keyword data, and reports in CSV or PDF format. If you wish to close your account, contact support to initiate the process. We retain your data for 30 days after account closure to allow for reconsideration before permanent deletion." }
  ];

  return (
    <>
      <Helmet>
        <title>Account Profile & Settings | SERPfinity</title>
        <meta name="description" content="Manage your SERPfinity account profile, subscription settings, notification preferences, and team access. Customize your experience and control your account security." />
        <meta name="keywords" content="SERPfinity account, profile settings, subscription management, notification preferences, team management, account security, SERPfinity settings" />
        <link rel="canonical" href="https://thedigitalhall.club/profile" />
        <meta property="og:title" content="Account Profile | SERPfinity" />
        <meta property="og:description" content="Manage your SERPfinity account settings, subscription, and preferences." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":PROFILE_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <div className="min-h-screen bg-[#0a0f1a] pt-20 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* Profile Header */}
        <div className="bg-gradient-to-r from-[#0f1729] to-[#141d30] rounded-2xl border border-gray-800/50 p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${getPlanColor()} flex items-center justify-center text-white text-2xl font-bold shadow-lg flex-shrink-0`}>
              {getInitials()}
            </div>
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl font-bold text-white">
                {profile.firstName || ''} {profile.lastName || ''}
              </h1>
              <p className="text-gray-400 mt-1">{profile.email || ''}</p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPlanBadgeColor()}`}>
                  {planLabel} Plan
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor()}`}>
                  {subscriptionStatus === 'active' ? 'Active' : subscriptionStatus === 'trialing' ? 'Trial' : subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)}
                </span>
                {profile.company && (
                  <span className="text-gray-500 text-sm">{profile.company}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-[#0f1729] rounded-xl p-1 border border-gray-800/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-[#0f1729] rounded-2xl border border-gray-800/50 overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-gray-800/50">
              <h2 className="text-lg font-semibold text-white">Personal Information</h2>
              <p className="text-gray-400 text-sm mt-1">Update your personal details and company information</p>
            </div>
            <form onSubmit={handleProfileSubmit} className="p-6 sm:p-8 space-y-6">
              {profileMessage && (
                <div className={`p-4 rounded-xl text-sm font-medium ${
                  profileMessage.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {profileMessage.text}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                    placeholder="Enter last name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                <input
                  type="email"
                  value={profile.email || ''}
                  disabled
                  className="w-full px-4 py-3 bg-[#0a0f1a]/50 border border-gray-700/30 rounded-xl text-gray-500 cursor-not-allowed"
                />
                <p className="text-gray-600 text-xs mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company / Organization</label>
                <input
                  type="text"
                  value={profileData.company}
                  onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  placeholder="Enter company name (optional)"
                />
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
                <p className="text-gray-500 text-xs">Member since {formatDate(profile.createdAt)}</p>
                <button
                  type="submit"
                  disabled={profileLoading}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-xl hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/20"
                >
                  {profileLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Current Plan Card */}
            <div className="bg-[#0f1729] rounded-2xl border border-gray-800/50 overflow-hidden">
              <div className="p-6 sm:p-8 border-b border-gray-800/50">
                <h2 className="text-lg font-semibold text-white">Current Plan</h2>
                <p className="text-gray-400 text-sm mt-1">Your current subscription and usage details</p>
              </div>
              <div className="p-6 sm:p-8">
                <div className={`bg-gradient-to-br ${getPlanColor()} rounded-xl p-6 mb-6`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Current Plan</p>
                      <h3 className="text-3xl font-bold text-white mt-1">{planLabel}</h3>
                      {subscriptionStatus === 'active' && (
                        <p className="text-white/70 text-sm mt-2">
                          {profile.subscriptionEndDate ? `Renews ${formatDate(profile.subscriptionEndDate)}` : 'Active subscription'}
                        </p>
                      )}
                      {subscriptionStatus === 'trialing' && profile.trialEndDate && (
                        <p className="text-white/70 text-sm mt-2">Trial ends {formatDate(profile.trialEndDate)}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-[#0a0f1a] rounded-xl p-4 border border-gray-800/30">
                    <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Scans Used</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {scansThisMonth}
                      <span className="text-sm text-gray-500 font-normal">
                        /{typeof scansLimit === 'number' ? scansLimit : scansLimit}
                      </span>
                    </p>
                    {typeof scansLimit === 'number' && (
                      <div className="mt-2 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((scansThisMonth / scansLimit) * 100, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div className="bg-[#0a0f1a] rounded-xl p-4 border border-gray-800/30">
                    <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Status</p>
                    <p className="text-lg font-semibold text-white mt-1 capitalize">{subscriptionStatus || 'Inactive'}</p>
                  </div>
                  <div className="bg-[#0a0f1a] rounded-xl p-4 border border-gray-800/30">
                    <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">
                      {plan === 'free' ? 'Started' : 'Billing Period'}
                    </p>
                    <p className="text-lg font-semibold text-white mt-1">
                      {plan === 'free'
                        ? formatDate(profile.createdAt)
                        : profile.subscriptionStartDate
                          ? formatDate(profile.subscriptionStartDate)
                          : 'N/A'
                      }
                    </p>
                  </div>
                </div>

                {/* Plan Features Quick Look */}
                {planLimits && (
                  <div className="bg-[#0a0f1a] rounded-xl p-5 border border-gray-800/30 mb-6">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">Plan Features</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {planLimits.scansPerMonth && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-400 text-sm">{planLimits.scansPerMonth} scans/mo</span>
                        </div>
                      )}
                      {planLimits.features && planLimits.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-400 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upgrade / Manage Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-xl hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg shadow-orange-500/20 text-center"
                  >
                    {plan === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                  </button>
                  {plan !== 'free' && (
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="flex-1 px-6 py-3 bg-[#0a0f1a] border border-gray-700/50 text-gray-300 font-medium rounded-xl hover:bg-gray-800/50 hover:text-white transition-all text-center"
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="bg-[#0f1729] rounded-2xl border border-gray-800/50 overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-gray-800/50">
              <h2 className="text-lg font-semibold text-white">Change Password</h2>
              <p className="text-gray-400 text-sm mt-1">Update your password to keep your account secure</p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="p-6 sm:p-8 space-y-6">
              {passwordMessage && (
                <div className={`p-4 rounded-xl text-sm font-medium ${
                  passwordMessage.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {passwordMessage.text}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  placeholder="Enter new password (min. 8 characters)"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0a0f1a] border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>
              <div className="pt-4 border-t border-gray-800/50">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-xl hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/20"
                >
                  {passwordLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Changing...
                    </span>
                  ) : 'Update Password'}
                </button>
              </div>
            </form>

            {/* Account Info */}
            <div className="p-6 sm:p-8 border-t border-gray-800/50 bg-[#0a0f1a]/30">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">Account Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Last Login</span>
                  <p className="text-gray-300 mt-0.5">{formatDate(profile.lastLoginAt)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Account Created</span>
                  <p className="text-gray-300 mt-0.5">{formatDate(profile.createdAt)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Email Verified</span>
                  <p className={`mt-0.5 ${profile.emailVerified ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {profile.emailVerified ? 'Verified' : 'Not Verified'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Account Status</span>
                  <p className="text-emerald-400 mt-0.5">Active</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      <FAQSection items={PROFILE_FAQ} />
    </>
  );
};

export default Profile;
